import { RefObject, useCallback, useEffect, useState } from 'react';

import { EMPTY_FUNCTION } from '../../../constants';

interface FocusProps {
  ref: RefObject<HTMLInputElement>;
  auto_focus?: boolean;
  filterable?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onOpen?: () => void;
}

export const useFocus = ({
  ref,
  auto_focus = false,
  filterable = true,
  onFocus: onFocusProp = EMPTY_FUNCTION,
  onBlur: onBlurProp = EMPTY_FUNCTION,
  onOpen = EMPTY_FUNCTION,
}: FocusProps) => {
  const [is_focused, setFocus] = useState(false);

  useEffect(() => {
    if (auto_focus && ref.current && !is_focused) {
      requestAnimationFrame(() => ref.current?.focus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto_focus, ref.current]);

  const onFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocus(true);
      onFocusProp(e);

      if (!filterable || !auto_focus) {
        onOpen();
      }
    },
    [filterable, auto_focus, onFocusProp, onOpen]
  );

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const relatedTarget = e.relatedTarget as HTMLElement | null;

      if (relatedTarget !== ref.current) {
        setFocus(false);
        onBlurProp(e);
      }
    },
    [ref, onBlurProp]
  );

  return { is_focused, onFocus, onBlur };
};
